import type { ReactNode } from 'react';

import AppLauncherWidget from '@/features/widgets/app-launcher/AppLauncherWidget';
import ElectricityWidget from '@/features/widgets/electricity/ElectricityWidget';
import HslBusesWidget from '@/features/widgets/hsl-buses/HslBusesWidget';
import SaunaWidget from '@/features/widgets/sauna/SaunaWidget';
import SensorsWidget from '@/features/widgets/sensors/SensorsWidget';
import StocksWidget from '@/features/widgets/stocks/StocksWidget';
import WeatherWidget from '@/features/widgets/weather/WeatherWidget';
import { cn } from '@/lib/cn';

import { useDashboardBreakpoint } from './hooks/useDashboardBreakpoint';

/** Grid cell that stretches its single widget child to fill the cell. */
function Cell({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid min-h-0', className)}>{children}</div>;
}

/** Wide (≥1280): buses column + weather/electricity + sensors/stocks; sauna shares its row with the app dock. */
function WideLayout() {
  return (
    <div className="tablet-hz:min-h-[calc(100dvh-8rem)] flex flex-col gap-4">
      <div className="grid min-h-0 flex-1 grid-cols-[1.15fr_1.25fr_1fr] grid-rows-2 gap-4">
        <Cell className="row-span-2">
          <HslBusesWidget />
        </Cell>
        <Cell>
          <WeatherWidget />
        </Cell>
        <Cell>
          <SensorsWidget />
        </Cell>
        <Cell>
          <ElectricityWidget />
        </Cell>
        <Cell>
          <StocksWidget />
        </Cell>
      </div>
      <div className="flex items-center gap-4">
        <Cell className="min-w-0 flex-1">
          <SaunaWidget />
        </Cell>
        <AppLauncherWidget />
      </div>
    </div>
  );
}

/** Tablet (820–1279): stacked essentials, then a sensors+sauna | stocks pair, then the app dock. */
function TabletLayout() {
  return (
    <div className="flex flex-col gap-4">
      <HslBusesWidget />
      <WeatherWidget />
      <ElectricityWidget />
      <div className="grid grid-cols-2 gap-4">
        <div className="flex min-w-0 flex-col gap-4">
          <SensorsWidget />
          <SaunaWidget />
        </div>
        <Cell className="min-w-0">
          <StocksWidget />
        </Cell>
      </div>
      <div className="flex justify-center pt-1">
        <AppLauncherWidget />
      </div>
    </div>
  );
}

/** Phone (<820): single scrolling column. */
function PhoneLayout() {
  return (
    <div className="flex flex-col gap-3">
      <HslBusesWidget />
      <WeatherWidget />
      <ElectricityWidget />
      <SaunaWidget />
      <SensorsWidget />
      <StocksWidget />
      <div className="flex justify-center pt-1">
        <AppLauncherWidget />
      </div>
    </div>
  );
}

export default function DashboardGrid() {
  const breakpoint = useDashboardBreakpoint();
  if (breakpoint === 'wide') return <WideLayout />;
  if (breakpoint === 'tablet') return <TabletLayout />;
  return <PhoneLayout />;
}
